'use client';
import { useState, useRef, useEffect, useMemo } from 'react';
import { Article } from 'shared/src/types/article.type';
import { Locale, LocaleDetails } from 'shared/src/types/languages.type';
import { IoSettingsOutline, IoClose, IoCheckmarkCircle, IoAddCircle, IoRefresh, IoPlay } from 'react-icons/io5';
import { PublishArticle, UnpublishArticle, GeneratePosts } from '@/server/articles/publish-article';
import { updateImage } from '@/server/articles/update-image';
import { sendPostToEmail } from '@/server/articles/send-post-to-email';
import { useAuth } from '../../auth-context';
import { articleAdminUIContent } from '@/lib/ui-content/article-ui-content';
import { PlatformSelector } from './platform-selector';
import { DeleteArticleModal } from './delete-article-modal';

type AdminControlCenterProps = {
  article: Article;
  lang: Locale;
  locale: LocaleDetails;
  domain: string;
  clientId: string;
  contactEmail?: string;
};

type SocialPost = {
  content: string;
  platform: 'linkedIn' | 'twitter' | 'facebook' | 'instagram';
  id?: string;
  selected?: boolean;
  // New fields for enhanced post data
  percentage?: string;
  generatedAt?: string;
  aiAnalysis?: {
    improvementSuggestions?: string;
    targetAudiencePerspective?: string;
  };
};

export const AdminControlCenter = ({ article, lang, locale, domain, clientId, contactEmail }: AdminControlCenterProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const [activeTab, setActiveTab] = useState<'general' | 'posts'>('general');
  const [loading, setLoading] = useState(false);
  const [publishLoading, setPublishLoading] = useState(false);
  const [selectedPlatforms, setSelectedPlatforms] = useState<string[]>([]);
  const [selectedPostGenPlatforms, setSelectedPostGenPlatforms] = useState<string[]>([]);
  const [posts, setPosts] = useState<SocialPost[]>([]);
  const [selectedPostId, setSelectedPostId] = useState<string | null>(null);
  const [showScheduleModal, setShowScheduleModal] = useState(false);
  const [scheduledDateTime, setScheduledDateTime] = useState('');
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [showAiAnalysis, setShowAiAnalysis] = useState<{ [key: string]: boolean }>({});
  const adminRef = useRef<HTMLDivElement>(null);
  const { user } = useAuth();
  const uiContent = articleAdminUIContent[locale.languageCode] || articleAdminUIContent.en;

  // Helper function to normalize platform names
  const normalizePlatformForBackend = (platform: string): string => {
    return platform === 'linkedIn' ? 'linkedin' : platform;
  };

  const normalizePlatformForFrontend = (platform: string): string => {
    return platform === 'linkedin' ? 'linkedIn' : platform;
  };

  // Helper function to format dates in a user-friendly way
  const formatGeneratedDate = (dateString: string): string => {
    const date = new Date(dateString);
    const now = new Date();
    const diffInMinutes = Math.floor((now.getTime() - date.getTime()) / (1000 * 60));

    if (diffInMinutes < 60) {
      return `${diffInMinutes} min ago`;
    } else if (diffInMinutes < 1440) {
      // Less than 24 hours
      const hours = Math.floor(diffInMinutes / 60);
      return `${hours}h ago`;
    } else {
      return date.toLocaleDateString() + ' ' + date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    }
  };

  // Helper function to determine the winning post based on highest percentage
  const getWinningPostId = (platformPosts: SocialPost[]): string | null => {
    if (platformPosts.length === 0) return null;

    let highestScore = -1;
    let winningPostId = null;

    platformPosts.forEach((post) => {
      if (post.percentage) {
        const score = parseInt(post.percentage.replace('%', ''));
        if (score > highestScore) {
          highestScore = score;
          winningPostId = post.id || null;
        }
      }
    });

    return winningPostId;
  };

  // Function to format content based on platform capabilities
  const formatContentForPlatform = (content: string, platform: 'linkedIn' | 'twitter' | 'facebook' | 'instagram') => {
    // Platform HTML support capabilities
    const platformSupports = {
      linkedIn: { html: true, bold: true, italic: true, links: true },
      facebook: { html: true, bold: true, italic: true, links: true },
      instagram: { html: false, bold: false, italic: false, links: false }, // Instagram doesn't support HTML formatting
      twitter: { html: false, bold: false, italic: false, links: true }, // Twitter supports links but not HTML formatting
    };

    const support = platformSupports[platform];

    if (!support.html) {
      // Strip HTML tags and convert to plain text for platforms that don't support HTML
      return content
        .replace(/<br\s*\/?>/gi, '\n') // Convert <br> to line breaks
        .replace(/<\/p><p[^>]*>/gi, '\n\n') // Convert paragraph breaks to double line breaks
        .replace(/<p[^>]*>/gi, '') // Remove opening <p> tags
        .replace(/<\/p>/gi, '') // Remove closing </p> tags
        .replace(/<strong[^>]*>(.*?)<\/strong>/gi, support.bold ? '**$1**' : '$1') // Convert bold to markdown if supported
        .replace(/<b[^>]*>(.*?)<\/b>/gi, support.bold ? '**$1**' : '$1')
        .replace(/<em[^>]*>(.*?)<\/em>/gi, support.italic ? '*$1*' : '$1') // Convert italic to markdown if supported
        .replace(/<i[^>]*>(.*?)<\/i>/gi, support.italic ? '*$1*' : '$1')
        .replace(/<[^>]*>/g, '') // Remove any remaining HTML tags
        .replace(/\n{3,}/g, '\n\n') // Limit consecutive line breaks to maximum of 2
        .trim();
    }

    // For platforms that support HTML, return formatted content
    return content;
  };

  // Get enabled platforms dynamically from article.posts
  const enabledPlatforms = useMemo(
    () => ({
      linkedIn:
        (Array.isArray(article.posts?.linkedIn) && article.posts.linkedIn.length > 0) ||
        (Array.isArray((article.posts as any)?.linkedin) && (article.posts as any).linkedin.length > 0),
      twitter: Array.isArray(article.posts?.twitter) && article.posts.twitter.length > 0,
      facebook: Array.isArray(article.posts?.facebook) && article.posts.facebook.length > 0,
      instagram: Array.isArray(article.posts?.instagram) && article.posts.instagram.length > 0,
    }),
    [article.posts],
  );

  // Get available platforms (only ones with actual posts)
  const availablePlatforms = useMemo(
    () =>
      Object.entries(enabledPlatforms)
        .filter(([_, hasContent]) => hasContent)
        .map(([platform, _]) => platform as 'linkedIn' | 'twitter' | 'facebook' | 'instagram'),
    [enabledPlatforms],
  );

  // Set default platform to first available platform
  const defaultPlatform = availablePlatforms[0] || 'linkedIn';

  const [postPlatform, setPostPlatform] = useState<'linkedIn' | 'twitter' | 'facebook' | 'instagram'>(defaultPlatform);

  useEffect(() => {
    // Load existing posts from article dynamically
    if (article.posts) {
      const loadedPosts: SocialPost[] = [];
      Object.entries(article.posts).forEach(([platform, postArray]) => {
        if (Array.isArray(postArray) && postArray.length > 0) {
          postArray.forEach((postData, index) => {
            // Normalize platform names: convert 'linkedin' to 'linkedIn'
            const normalizedPlatform = normalizePlatformForFrontend(platform);

            // Handle both old format (string) and new format (object with post property)
            let content: string;
            let percentage: string | undefined;
            let generatedAt: string | undefined;
            let aiAnalysis: any | undefined;

            if (typeof postData === 'string') {
              // Old format: posts are just strings
              content = postData;
            } else if (postData && typeof postData === 'object' && 'post' in postData) {
              // New format: posts are objects with additional metadata
              const postObj = postData as any;
              content = postObj.post;
              percentage = postObj.percentage;
              generatedAt = postObj.generatedAt;
              aiAnalysis = postObj.aiAnalysis;
            } else {
              // Fallback for unexpected format
              content = String(postData);
            }

            loadedPosts.push({
              content,
              platform: normalizedPlatform as 'linkedIn' | 'twitter' | 'facebook' | 'instagram',
              id: `${normalizedPlatform}-${index}`,
              selected: false,
              percentage,
              generatedAt,
              aiAnalysis,
            });
          });
        }
      });
      setPosts(loadedPosts);
    }
  }, [article.posts]);

  useEffect(() => {
    // Update selected platform if current one is not available
    if (!availablePlatforms.includes(postPlatform) && availablePlatforms.length > 0) {
      setPostPlatform(availablePlatforms[0]);
    }

    // Switch to general tab if article is not published and current tab is posts
    if (!article.published && activeTab === 'posts') {
      setActiveTab('general');
    }
  }, [availablePlatforms, postPlatform, article.published, activeTab]);

  const handlePublish = async () => {
    const href = `${domain}${window.location.pathname}`;
    setPublishLoading(true);
    try {
      if (article.published) {
        await UnpublishArticle(lang, article.id, 'secret', user?.uid);
      } else {
        await PublishArticle({
          lang,
          id: article.id,
          href,
          uid: user?.uid ?? '',
          platforms: selectedPlatforms,
          articleTitle: article.title,
        });
      }
      window.location.reload();
    } catch (error) {
      console.error('Error publishing/unpublishing article:', error);
    } finally {
      setPublishLoading(false);
    }
  };

  const togglePlatform = (platform: string) => {
    setSelectedPlatforms((prev) => (prev.includes(platform) ? prev.filter((p) => p !== platform) : [...prev, platform]));
  };

  const togglePostGenPlatform = (platform: string) => {
    setSelectedPostGenPlatforms((prev) => (prev.includes(platform) ? prev.filter((p) => p !== platform) : [...prev, platform]));
  };

  const toggleAiAnalysis = (postId: string) => {
    setShowAiAnalysis((prev) => ({
      ...prev,
      [postId]: !prev[postId],
    }));
  };

  const handleDelete = () => {
    setShowDeleteModal(true);
  };

  const handleDeleteError = (error: string) => {
    setErrorMessage(error);
    // Auto-hide error after 5 seconds
    setTimeout(() => {
      setErrorMessage(null);
    }, 5000);
  };

  const handleDeleteCancel = () => {
    setShowDeleteModal(false);
  };

  const handleUpdateImage = async () => {
    setLoading(true);
    try {
      await updateImage({ lang, id: article.id, title: article.title });
      alert(uiContent.imageUpdateRequested);
      window.location.reload();
    } catch (error) {
      console.error('Error updating image:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSendEmail = async () => {
    if (!contactEmail) return;
    setLoading(true);
    try {
      const href = `${domain}${window.location.pathname}`;
      await sendPostToEmail({ lang, href, id: article.id, receiver: contactEmail });
      alert(uiContent.emailSentSuccessfully);
    } catch (error) {
      console.error('Error sending email:', error);
    } finally {
      setLoading(false);
    }
  };

  const generatePosts = async () => {
    if (selectedPostGenPlatforms.length === 0) {
      alert(uiContent.selectAtLeastOnePlatform);
      return;
    }

    setLoading(true);
    try {
      // Normalize platform names for backend (linkedIn -> linkedin)
      const normalizedPlatforms = selectedPostGenPlatforms.map(normalizePlatformForBackend);

      const result = await GeneratePosts({
        lang,
        id: article.id,
        platforms: normalizedPlatforms,
        clientId,
      });

      if (result.success) {
        // Refresh the page to load the newly generated posts
        window.location.reload();
      } else {
        console.error('Error generating posts:', result.error);
        alert(`Failed to generate posts: ${result.error}`);
      }
    } catch (error) {
      console.error('Error generating posts:', error);
      alert('An unexpected error occurred while generating posts');
    } finally {
      setLoading(false);
    }
  };

  const generatePostsForAllPlatforms = async () => {
    // Check if platforms are selected first
    if (selectedPostGenPlatforms.length === 0) {
      alert(uiContent.selectAtLeastOnePlatform);
      return;
    }

    setLoading(true);
    try {
      // Normalize platform names for backend (linkedIn -> linkedin)
      const normalizedPlatforms = selectedPostGenPlatforms.map(normalizePlatformForBackend);

      const result = await GeneratePosts({
        lang,
        id: article.id,
        platforms: normalizedPlatforms,
        clientId,
      });

      if (result.success) {
        // Refresh the page to load the newly generated posts
        window.location.reload();
      } else {
        console.error('Error generating posts for platforms:', result.error);
        alert(`Failed to generate posts: ${result.error}`);
      }
    } catch (error) {
      console.error('Error generating posts for platforms:', error);
      alert('An unexpected error occurred while generating posts');
    } finally {
      setLoading(false);
    }
  };

  const selectPost = (postId: string) => {
    setSelectedPostId(postId);
    setPosts(
      posts.map((post) => ({
        ...post,
        selected: post.id === postId,
      })),
    );
  };

  const publishPost = async (immediate = false) => {
    const selectedPost = posts.find((post) => post.id === selectedPostId);
    if (!selectedPost) {
      alert(uiContent.pleaseSelectPost);
      return;
    }

    if (!immediate) {
      setShowScheduleModal(true);
      return;
    }

    setLoading(true);
    try {
      const response = await fetch('https://inceptionai.app.n8n.cloud/webhook/publish_post', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          clientId,
          id: article.id,
          selectedPosts: [
            {
              postId: selectedPost.id,
              platform: selectedPost.platform,
              content: selectedPost.content,
            },
          ],
        }),
      });

      if (response.ok) {
        alert(uiContent.postPublishedSuccessfully);
        setSelectedPostId(null);
        setPosts(posts.map((post) => ({ ...post, selected: false })));
      }
    } catch (error) {
      console.error('Error publishing post:', error);
    } finally {
      setLoading(false);
    }
  };

  const schedulePost = async () => {
    const selectedPost = posts.find((post) => post.id === selectedPostId);
    if (!selectedPost || !scheduledDateTime) return;

    setLoading(true);
    try {
      // Get user's timezone
      const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;

      const response = await fetch('https://inceptionai.app.n8n.cloud/webhook/schedule_post', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          clientId,
          id: article.id,
          selectedPosts: [
            {
              postId: selectedPost.id,
              platform: selectedPost.platform,
              content: selectedPost.content,
            },
          ],
          scheduledDateTime: new Date(scheduledDateTime).toISOString(),
          timezone,
        }),
      });

      if (response.ok) {
        alert(uiContent.postSentSuccessfully || 'Post scheduled successfully!');
        setSelectedPostId(null);
        setShowScheduleModal(false);
        setScheduledDateTime('');
        setPosts(posts.map((post) => ({ ...post, selected: false })));
      }
    } catch (error) {
      console.error('Error scheduling post:', error);
    } finally {
      setLoading(false);
    }
  };

  const confirmPost = async (post: SocialPost) => {
    setLoading(true);
    try {
      const response = await fetch('https://inceptionai.app.n8n.cloud/webhook/post', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          clientId,
          id: article.id,
          indice: post.content,
          lang: locale.languageCode,
        }),
      });

      if (response.ok) {
        alert(uiContent.postSentSuccessfully);
      }
    } catch (error) {
      console.error('Error sending post:', error);
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) {
    return (
      <button
        className={
          'fixed right-2 top-[6.9rem] z-30 flex items-center justify-center rounded-full border border-neutral-900/10 bg-light p-1.5 dark:border-light dark:bg-dark xs:right-5'
        }
        onClick={() => setIsOpen(true)}
        aria-label={uiContent.adminControlCenter}
        title={uiContent.adminControlCenter}
      >
        <IoSettingsOutline className="h-6 w-6" />
      </button>
    );
  }

  console.log('POST: ', JSON.stringify(posts, null, 2));

  return (
    <div
      ref={adminRef}
      className="animate-fadeIn fixed inset-0 top-0 z-40 overflow-y-auto bg-black bg-opacity-30 backdrop-blur-sm"
      onClick={(e) => {
        if (e.target === adminRef.current) {
          setIsOpen(false);
        }
      }}
    >
      <div className="relative mx-auto flex w-full flex-col items-center justify-start bg-light px-4 pt-12 dark:bg-dark sm:px-[calc(7%+1rem)]">
        {/* Background gradient - same as original */}
        <div
          className="absolute top-0 z-0 h-full max-w-[100vw] max-sm:w-screen sm:w-screen"
          style={{
            background: `linear-gradient(to right, rgba(80, 147, 235, 0.13), rgba(135,136,233, 0.08), rgba(7,198,203, 0.1))`,
          }}
        />

        {/* Close button */}
        <button
          onClick={() => setIsOpen(false)}
          className="fixed right-4 top-16 z-[70] rounded-full bg-red-500 p-2 text-white shadow-lg transition-colors duration-200 hover:bg-red-600"
          aria-label={uiContent.close}
          title={uiContent.close}
        >
          <IoClose className="h-5 w-5" />
        </button>

        {/* Admin content in the exact same structure */}
        <div className={'z-10 flex'}>
          {/* {article?.thumbnail && (
            <div className="relative max-md:-ml-[4.2rem] max-md:w-[99dvw] max-sm:-ml-[2.1rem] max-xs:-ml-[1.4rem] lg:mx-2 lg:mt-3 lg:w-1/2">
              <Image
                className="aspect-[16/11] w-full rounded-md object-cover opacity-90 dark:brightness-90 max-lg:aspect-video max-xs:mx-auto"
                src={article.thumbnail}
                alt={article.title}
                width={400}
                height={300}
                priority
              />
            </div>
          )} */}

          <div className={'z-10 my-4 flex flex-col px-3'}>
            {/* Admin tabs */}
            <div className="mb-4 flex space-x-1 rounded-lg bg-gray-100 p-1 dark:bg-gray-800">
              <button
                onClick={() => setActiveTab('general')}
                className={`flex-1 rounded-md px-3 py-2 text-sm font-medium transition-colors ${
                  activeTab === 'general'
                    ? 'bg-white text-blue-600 shadow-sm dark:bg-gray-700 dark:text-blue-400'
                    : 'text-gray-600 hover:text-gray-900 dark:text-gray-400 dark:hover:text-gray-200'
                }`}
              >
                {uiContent.general}
              </button>
              {article.published && (
                <button
                  onClick={() => setActiveTab('posts')}
                  className={`flex-1 rounded-md px-3 py-2 text-sm font-medium transition-colors ${
                    activeTab === 'posts'
                      ? 'bg-white text-blue-600 shadow-sm dark:bg-gray-700 dark:text-blue-400'
                      : 'text-gray-600 hover:text-gray-900 dark:text-gray-400 dark:hover:text-gray-200'
                  }`}
                >
                  {uiContent.socialPosts}
                </button>
              )}
            </div>

            <h1 className="my-2 text-3xl font-semibold leading-tight max-lg:pb-7 max-sm:text-2xl lg:flex lg:flex-grow">
              {uiContent.adminControlCenter}
            </h1>
          </div>
        </div>

        {/* Admin content sections */}
        <div className="z-10 mx-auto mt-0 w-full pb-14 pt-2 max-sm:pb-12 lg:pt-4">
          {activeTab === 'general' && (
            <div className="space-y-6">
              <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
                <div className="rounded-xl bg-white/80 p-6 shadow-lg backdrop-blur-sm dark:bg-gray-800/80">
                  <h3 className="mb-4 text-lg font-semibold text-gray-900 dark:text-white">{uiContent.articleStatus}</h3>
                  <div className="mb-4 flex items-center gap-3">
                    <div className={`h-4 w-4 rounded-full ${article.published ? 'bg-green-500' : 'bg-yellow-500'}`} />
                    <span className="text-gray-600 dark:text-gray-300">
                      {article.published ? uiContent.published : uiContent.draft}
                    </span>
                  </div>

                  {!article.published && (
                    <PlatformSelector selectedPlatforms={selectedPlatforms} onTogglePlatform={togglePlatform} />
                  )}

                  <button
                    onClick={handlePublish}
                    disabled={publishLoading}
                    className={`w-full rounded-lg px-4 py-3 font-medium transition-colors ${
                      article.published ? 'bg-red-500 text-white hover:bg-red-600' : 'bg-green-500 text-white hover:bg-green-600'
                    } disabled:opacity-50`}
                  >
                    {publishLoading ? (
                      <IoRefresh className="mx-auto h-5 w-5 animate-spin" />
                    ) : article.published ? (
                      uiContent.unpublishArticle
                    ) : (
                      uiContent.publishArticle
                    )}
                  </button>
                </div>

                <div className="rounded-xl bg-white/80 p-6 shadow-lg backdrop-blur-sm dark:bg-gray-800/80">
                  <h3 className="mb-4 text-lg font-semibold text-gray-900 dark:text-white">{uiContent.quickActions}</h3>
                  <div className="space-y-3">
                    <button
                      onClick={handleUpdateImage}
                      disabled={loading}
                      className="w-full rounded-lg bg-blue-500 px-4 py-3 text-white transition-colors hover:bg-blue-600 disabled:opacity-50"
                    >
                      {uiContent.updateImage}
                    </button>
                    {contactEmail && article.published && (
                      <button
                        onClick={handleSendEmail}
                        disabled={loading}
                        className="w-full rounded-lg bg-purple-500 px-4 py-3 text-white transition-colors hover:bg-purple-600 disabled:opacity-50"
                      >
                        {uiContent.sendToEmail}
                      </button>
                    )}
                    {!article.published && (
                      <button
                        onClick={handleDelete}
                        disabled={loading}
                        className="w-full rounded-lg bg-red-500 px-4 py-3 text-white transition-colors hover:bg-red-600 disabled:opacity-50"
                      >
                        {uiContent.deleteArticle}
                      </button>
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'posts' && article.published && (
            <div className="space-y-6">
              <div className="rounded-xl bg-white/80 p-6 shadow-lg backdrop-blur-sm dark:bg-gray-800/80">
                <h3 className="mb-4 text-lg font-semibold text-gray-900 dark:text-white">{uiContent.generateSocialPosts}</h3>

                {/* Platform Selection for Post Generation */}
                <div className="mb-6">
                  <h4 className="mb-3 text-sm font-medium text-gray-700 dark:text-gray-300">
                    {uiContent.selectPlatformsToGenerate}
                  </h4>
                  <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
                    {['linkedIn', 'twitter', 'facebook', 'instagram'].map((platform) => (
                      <button
                        key={platform}
                        onClick={() => togglePostGenPlatform(platform)}
                        className={`rounded-lg border px-4 py-3 text-sm font-medium transition-all ${
                          selectedPostGenPlatforms.includes(platform)
                            ? 'border-blue-500 bg-blue-50 text-blue-700 shadow-sm dark:border-blue-400 dark:bg-blue-900/20 dark:text-blue-300'
                            : 'border-gray-300 bg-white text-gray-700 hover:border-gray-400 hover:bg-gray-50 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-300 dark:hover:border-gray-500 dark:hover:bg-gray-600'
                        }`}
                      >
                        <div className="flex items-center justify-center gap-2">
                          {selectedPostGenPlatforms.includes(platform) && <IoCheckmarkCircle className="h-4 w-4" />}
                          {platform === 'linkedIn' ? uiContent.linkedin : platform.charAt(0).toUpperCase() + platform.slice(1)}
                        </div>
                      </button>
                    ))}
                  </div>
                  {selectedPostGenPlatforms.length === 0 && (
                    <p className="mt-2 text-xs text-amber-600 dark:text-amber-400">{uiContent.selectAtLeastOnePlatform}</p>
                  )}
                </div>

                <div className="mb-4 space-y-4">
                  {availablePlatforms.length > 0 && (
                    <div
                      className={`grid gap-3 ${availablePlatforms.length <= 2 ? 'grid-cols-1 sm:grid-cols-2' : 'grid-cols-2 sm:grid-cols-4'}`}
                    >
                      {availablePlatforms.includes('linkedIn') && (
                        <button
                          onClick={() => setPostPlatform('linkedIn')}
                          className={`rounded-lg border px-4 py-3 text-sm font-medium transition-colors ${
                            postPlatform === 'linkedIn'
                              ? 'border-blue-500 bg-blue-50 text-blue-700 dark:border-blue-400 dark:bg-blue-900/20 dark:text-blue-300'
                              : 'border-gray-300 bg-white text-gray-700 hover:bg-gray-50 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-300 dark:hover:bg-gray-600'
                          }`}
                        >
                          {uiContent.linkedin}
                        </button>
                      )}
                      {availablePlatforms.includes('twitter') && (
                        <button
                          onClick={() => setPostPlatform('twitter')}
                          className={`rounded-lg border px-4 py-3 text-sm font-medium transition-colors ${
                            postPlatform === 'twitter'
                              ? 'border-blue-500 bg-blue-50 text-blue-700 dark:border-blue-400 dark:bg-blue-900/20 dark:text-blue-300'
                              : 'border-gray-300 bg-white text-gray-700 hover:bg-gray-50 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-300 dark:hover:bg-gray-600'
                          }`}
                        >
                          {uiContent.twitter}
                        </button>
                      )}
                      {availablePlatforms.includes('facebook') && (
                        <button
                          onClick={() => setPostPlatform('facebook')}
                          className={`rounded-lg border px-4 py-3 text-sm font-medium transition-colors ${
                            postPlatform === 'facebook'
                              ? 'border-blue-500 bg-blue-50 text-blue-700 dark:border-blue-400 dark:bg-blue-900/20 dark:text-blue-300'
                              : 'border-gray-300 bg-white text-gray-700 hover:bg-gray-50 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-300 dark:hover:bg-gray-600'
                          }`}
                        >
                          {uiContent.facebook}
                        </button>
                      )}
                      {availablePlatforms.includes('instagram') && (
                        <button
                          onClick={() => setPostPlatform('instagram')}
                          className={`rounded-lg border px-4 py-3 text-sm font-medium transition-colors ${
                            postPlatform === 'instagram'
                              ? 'border-blue-500 bg-blue-50 text-blue-700 dark:border-blue-400 dark:bg-blue-900/20 dark:text-blue-300'
                              : 'border-gray-300 bg-white text-gray-700 hover:bg-gray-50 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-300 dark:hover:bg-gray-600'
                          }`}
                        >
                          {uiContent.instagram}
                        </button>
                      )}
                    </div>
                  )}
                  {availablePlatforms.length > 0 ? (
                    <button
                      onClick={generatePosts}
                      disabled={loading || selectedPostGenPlatforms.length === 0}
                      className="flex w-full items-center justify-center gap-2 rounded-lg bg-gradient-to-r from-blue-500 to-blue-600 px-6 py-3 text-white shadow-lg transition-all hover:from-blue-600 hover:to-blue-700 hover:shadow-xl disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:shadow-lg"
                    >
                      {loading ? <IoRefresh className="h-5 w-5 animate-spin" /> : <IoAddCircle className="h-5 w-5" />}
                      {loading ? uiContent.generating : uiContent.generatePostsForSelectedPlatforms}
                    </button>
                  ) : (
                    <div className="space-y-4 rounded-xl border-2 border-dashed border-gray-300 bg-gradient-to-br from-gray-50 to-gray-100 p-6 text-center dark:border-gray-600 dark:from-gray-700 dark:to-gray-800">
                      <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-blue-100 dark:bg-blue-900/20">
                        <IoAddCircle className="h-8 w-8 text-blue-500 dark:text-blue-400" />
                      </div>
                      <div>
                        <h4 className="mb-2 font-medium text-gray-900 dark:text-white">{uiContent.noSocialMediaPostsYet}</h4>
                        <p className="mb-4 text-sm text-gray-500 dark:text-gray-400">{uiContent.generateEngagingPosts}</p>
                      </div>
                      <button
                        onClick={() => generatePostsForAllPlatforms()}
                        disabled={loading || selectedPostGenPlatforms.length === 0}
                        className="flex items-center justify-center gap-2 rounded-lg bg-gradient-to-r ml-auto from-blue-500 to-blue-600 px-6 py-3 text-white shadow-lg transition-all hover:from-blue-600 hover:to-blue-700 hover:shadow-xl disabled:opacity-50"
                      >
                        {loading ? <IoRefresh className="h-5 w-5 animate-spin" /> : <IoAddCircle className="h-5 w-5" />}
                        {loading ? uiContent.generating : uiContent.generatePostsForSelectedPlatforms}
                      </button>
                    </div>
                  )}
                </div>
              </div>

              {posts && posts.length > 0 && (
                <div className="rounded-xl bg-white/80 p-6 shadow-lg backdrop-blur-sm dark:bg-gray-800/80">
                  <div className="sticky top-16 z-10 mb-4 flex items-center justify-between rounded-lg border border-gray-200 bg-white/90 p-3 backdrop-blur-sm dark:border-gray-700 dark:bg-gray-800/90">
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white">{uiContent.generatedPosts}</h3>
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => publishPost(true)}
                        disabled={loading || !selectedPostId}
                        className="flex items-center gap-2 rounded-lg bg-gradient-to-r from-green-500 to-green-600 px-4 py-2 text-white shadow-lg transition-all hover:from-green-600 hover:to-green-700 hover:shadow-xl disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:shadow-lg"
                        title={!selectedPostId ? uiContent.pleaseSelectPost : ''}
                      >
                        {loading ? <IoRefresh className="h-4 w-4 animate-spin" /> : <IoPlay className="h-4 w-4" />}
                        {uiContent.publishNow}
                      </button>
                      <button
                        onClick={() => publishPost(false)}
                        disabled={loading || !selectedPostId}
                        className="flex items-center gap-2 rounded-lg bg-gradient-to-r from-blue-500 to-blue-600 px-4 py-2 text-white shadow-lg transition-all hover:from-blue-600 hover:to-blue-700 hover:shadow-xl disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:shadow-lg"
                        title={!selectedPostId ? uiContent.pleaseSelectPost : ''}
                      >
                        <IoAddCircle className="h-4 w-4" />
                        {uiContent.schedulePost}
                      </button>
                    </div>
                  </div>
                  <div className="space-y-4 overflow-y-auto">
                    {posts
                      .filter((post) => post.platform === postPlatform)
                      .map((post, index) => (
                        <div
                          key={post.id || index}
                          className={`cursor-pointer rounded-lg border p-4 transition-all hover:shadow-md ${
                            post.selected
                              ? 'border-blue-500 bg-blue-50 shadow-lg dark:border-blue-400 dark:bg-blue-900/20'
                              : getWinningPostId(posts.filter((p) => p.platform === postPlatform)) === post.id // Check if this is the winning post
                                ? 'border-yellow-400 bg-yellow-50 shadow-md dark:border-yellow-500 dark:bg-yellow-900/20'
                                : 'border-gray-200 bg-gray-50 hover:border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:hover:border-gray-500'
                          }`}
                          onClick={() => selectPost(post.id!)}
                        >
                          <div className="mb-3 flex items-center justify-between">
                            <div className="flex items-center gap-2">
                              <span className="rounded-full bg-gradient-to-r from-blue-500 to-purple-500 px-3 py-1 text-xs font-medium text-white">
                                {post.platform === 'linkedIn'
                                  ? 'LinkedIn'
                                  : post.platform.charAt(0).toUpperCase() + post.platform.slice(1)}
                              </span>
                              <span className="rounded-full bg-gray-100 px-2 py-1 text-xs text-gray-500 dark:bg-gray-600 dark:text-gray-300">
                                {post.platform === 'linkedIn' || post.platform === 'facebook'
                                  ? uiContent.richFormat
                                  : uiContent.plainText}
                              </span>
                              {post.percentage && (
                                <span className="rounded-full bg-green-100 px-2 py-1 text-xs text-green-700 dark:bg-green-900/20 dark:text-green-300">
                                  {uiContent.performanceScore}: {post.percentage}
                                </span>
                              )}
                              {getWinningPostId(posts.filter((p) => p.platform === postPlatform)) === post.id && (
                                <span className="rounded-full bg-yellow-100 px-2 py-1 text-xs text-yellow-700 dark:bg-yellow-900/20 dark:text-yellow-300">
                                  👑 {uiContent.winningPost}
                                </span>
                              )}
                              {post.selected && (
                                <div className="flex items-center gap-1 rounded-full bg-green-500 px-2 py-1 text-xs text-white">
                                  <IoCheckmarkCircle className="h-3 w-3" />
                                  {uiContent.selected}
                                </div>
                              )}
                            </div>
                            {post.aiAnalysis && (
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  toggleAiAnalysis(post.id!);
                                }}
                                className="rounded-lg bg-purple-100 px-2 py-1 text-xs text-purple-700 transition-colors hover:bg-purple-200 dark:bg-purple-900/20 dark:text-purple-300 dark:hover:bg-purple-900/40"
                              >
                                {showAiAnalysis[post.id!] ? uiContent.hideAiAnalysis : uiContent.showAiAnalysis}
                              </button>
                            )}
                          </div>

                          {/* Post metadata */}
                          {post.generatedAt && (
                            <div className="mb-2 text-xs text-gray-500 dark:text-gray-400">
                              {uiContent.generatedOn}: {formatGeneratedDate(post.generatedAt)}
                            </div>
                          )}

                          <div className="rounded-md bg-gradient-to-br from-gray-50 to-gray-100 p-4 dark:from-gray-600 dark:to-gray-700">
                            <div className="mb-2 text-xs font-medium text-gray-500 dark:text-gray-400">
                              {uiContent.previewFor}{' '}
                              {post.platform === 'linkedIn'
                                ? uiContent.linkedin
                                : post.platform.charAt(0).toUpperCase() + post.platform.slice(1)}
                              :
                            </div>
                            <div className="text-sm text-gray-700 dark:text-gray-300">
                              {post.platform === 'linkedIn' || post.platform === 'facebook' ? (
                                <div
                                  dangerouslySetInnerHTML={{ __html: formatContentForPlatform(post.content, post.platform) }}
                                  className="prose prose-sm dark:prose-invert max-w-none [&>p]:mb-2 [&>p]:last:mb-0"
                                />
                              ) : (
                                <pre className="whitespace-pre-wrap font-sans">
                                  {formatContentForPlatform(post.content, post.platform)}
                                </pre>
                              )}
                            </div>
                          </div>

                          {/* AI Analysis section */}
                          {post.aiAnalysis && showAiAnalysis[post.id!] && (
                            <div className="mt-4 rounded-lg bg-purple-50 p-4 dark:bg-purple-900/10">
                              <h4 className="mb-3 flex items-center gap-2 text-sm font-semibold text-purple-800 dark:text-purple-300">
                                <IoAddCircle className="h-4 w-4" />
                                {uiContent.aiInsights}
                              </h4>

                              {getWinningPostId(posts.filter((p) => p.platform === postPlatform)) === post.id && (
                                <div className="mb-3 rounded-md bg-green-100 p-2 dark:bg-green-900/20">
                                  <div className="text-xs font-medium text-green-800 dark:text-green-300">
                                    👑 {uiContent.winningPost}
                                  </div>
                                </div>
                              )}

                              {post.aiAnalysis.targetAudiencePerspective && (
                                <div className="mb-3">
                                  <div className="mb-1 text-xs font-medium text-purple-700 dark:text-purple-300">
                                    {uiContent.targetAudiencePerspective}:
                                  </div>
                                  <div className="text-xs text-gray-600 dark:text-gray-300">
                                    {post.aiAnalysis.targetAudiencePerspective}
                                  </div>
                                </div>
                              )}

                              {post.aiAnalysis.improvementSuggestions && (
                                <div className="mb-3">
                                  <div className="mb-1 text-xs font-medium text-purple-700 dark:text-purple-300">
                                    {uiContent.improvementSuggestions}:
                                  </div>
                                  <div className="text-xs text-gray-600 dark:text-gray-300">
                                    {post.aiAnalysis.improvementSuggestions}
                                  </div>
                                </div>
                              )}
                            </div>
                          )}

                          <div className="mt-3 text-xs text-gray-500 dark:text-gray-400">
                            {post.selected ? (
                              <span className="font-medium text-green-600 dark:text-green-400">✓ {uiContent.readyToPublish}</span>
                            ) : (
                              uiContent.clickToSelectPost
                            )}
                          </div>
                        </div>
                      ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Schedule Modal */}
      {showScheduleModal && (
        <div className="fixed inset-0 z-[80] flex items-center justify-center bg-black bg-opacity-50 backdrop-blur-sm">
          <div className="sticky top-1/2 mx-4 w-full max-w-md -translate-y-1/2 transform rounded-xl bg-white p-6 shadow-2xl dark:bg-gray-800">
            <div className="mb-4 flex items-center gap-3">
              <div className="rounded-full bg-blue-100 p-2 dark:bg-blue-900/20">
                <IoAddCircle className="h-5 w-5 text-blue-500 dark:text-blue-400" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">{uiContent.schedulePost}</h3>
            </div>
            <div className="mb-6">
              <label className="mb-3 block text-sm font-medium text-gray-700 dark:text-gray-300">
                {uiContent.selectDateAndTime}
              </label>
              <input
                type="datetime-local"
                value={scheduledDateTime}
                onChange={(e) => setScheduledDateTime(e.target.value)}
                min={new Date().toISOString().slice(0, 16)}
                className="w-full rounded-lg border border-gray-300 px-4 py-3 text-sm shadow-sm transition-colors focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20 dark:border-gray-600 dark:bg-gray-700 dark:text-white dark:focus:border-blue-400 dark:focus:ring-blue-400/20"
              />
              <p className="mt-2 text-xs text-gray-500 dark:text-gray-400">
                {uiContent.yourTimezone}: {Intl.DateTimeFormat().resolvedOptions().timeZone}
              </p>
            </div>
            <div className="flex justify-end gap-3">
              <button
                onClick={() => {
                  setShowScheduleModal(false);
                  setScheduledDateTime('');
                }}
                className="rounded-lg border border-gray-300 px-4 py-2 text-sm text-gray-700 transition-colors hover:bg-gray-50 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-700"
              >
                {uiContent.cancel}
              </button>
              <button
                onClick={schedulePost}
                disabled={loading || !scheduledDateTime}
                className="flex items-center gap-2 rounded-lg bg-gradient-to-r from-blue-500 to-blue-600 px-4 py-2 text-sm text-white shadow-lg transition-all hover:from-blue-600 hover:to-blue-700 hover:shadow-xl disabled:opacity-50"
              >
                {loading ? <IoRefresh className="h-4 w-4 animate-spin" /> : <IoAddCircle className="h-4 w-4" />}
                {uiContent.schedule}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Error Message Display */}
      {errorMessage && (
        <div className="fixed bottom-4 right-4 z-[90] animate-fadeIn rounded-lg bg-red-500 p-4 text-white shadow-lg">
          <div className="flex items-center gap-3">
            <div className="rounded-full bg-red-600 p-1">
              <IoClose className="h-4 w-4" />
            </div>
            <div>
              <p className="font-medium">{uiContent.errorOccurred}</p>
              <p className="text-sm">{errorMessage}</p>
            </div>
            <button onClick={() => setErrorMessage(null)} className="ml-auto rounded-full p-1 hover:bg-red-600">
              <IoClose className="h-4 w-4" />
            </button>
          </div>
        </div>
      )}

      {/* Delete Article Modal */}
      {showDeleteModal && (
        <DeleteArticleModal
          lang={lang}
          locale={locale}
          articleId={article.id}
          articleTitle={article.title}
          onError={handleDeleteError}
          onCancel={handleDeleteCancel}
        />
      )}
    </div>
  );
};
